'use client';

import {
	AMM_RESERVE_PRECISION_EXP,
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	PRICE_PRECISION,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	UIOrderType,
	TRADE_PRECISION,
	getMarketStepSizeDecimals,
	NumLib,
} from '@velocity-exchange/common';
import React, { useMemo } from 'react';
import ValueDisplay from 'src/components/ValueDisplay';
import { trimDisplayZeros } from 'src/utils/formatNumber/trimDisplayZeros';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import {
	capStringFractionDigits,
	formatText,
} from '@velocity-exchange/common/format';
import { PRESETS, withDecimals } from 'src/utils/formatNumber/presets';

import {
	useCurrentMarketStepSizeBn,
	useMarketSizeDigits,
	useMarketStepSize,
} from 'src/hooks/useMarketStepSize';
import { calculateAveragePrice } from 'src/utils/scaledOrders';
import usePreviewOrder from 'src/components/TradeForm/hooks/usePreviewOrder';
import useUserAccountIsReady from 'src/hooks/useUserAccountIsReady';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { ZERO } from 'src/constants/constants';
import useTradeFormEstimatedLiquidationPrice from 'src/hooks/Liquidations/useTradeFormEstimatedLiquidationPrice';
import SwiftOfflineIndicator from 'src/components/SwiftOfflineIndicator';
import FeeTierValueDisplay from 'src/components/TradeForm/FeeTierValueDisplay';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import LiquidationPriceDisplay from './LiquidationPriceDisplay';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import BottomDrawerSlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/BottomDrawerSlippageToleranceInput';
import CollapsibleSlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/CollapsibleSlippageToleranceInput';
import LimitOraclePriceDisplay from 'src/components/TradeForm/TradeDetails/LimitOraclePriceDisplay';
import EstimatedProfit from '../EstimatedProfit';
import { MarginRequiredTradeDetails } from './MarginRequiredTradeDetails';
import useSizeInputSource from 'src/components/TradeForm/SizeInput/hooks/useSizeInputSource';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

interface PerpTradeDetailsProps {
	orderType: UIOrderType;
	showSlippageTolerance?: boolean;
	marginRequired?: BN;
}

const PerpTradeDetails = ({
	orderType,
	showSlippageTolerance,
	marginRequired,
}: PerpTradeDetailsProps) => {
	const isMobileScreenSize = useIsMobileScreenSize();
	const tradeForm = useVelocityStore((s) => s.tradeForm);
	const [sizeInputSource] = useSizeInputSource();

	const {
		baseSizeStringValue,
		quoteSizeStringValue,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		scaledOrders: { sizeDistribution, orderCount },
	} = useVelocityStore((s) => s.tradeForm);

	const selectedMarketIndex = useVelocityStore(
		(s) => s.selectedMarket?.marketId?.marketIndex
	);
	const selectedMarket = useVelocityStore((s) => s.selectedMarket.current);
	const side = tradeForm.side;

	const baseAssetSymbol = selectedMarket?.uiSymbols.baseAssetSymbol ?? '';
	const marketDecimals = getMarketPriceDecimals(baseAssetSymbol);

	const currentAccountPositions = useCurrentAccountPositions();
	const currentPosition = useMemo(
		() =>
			currentAccountPositions.find(
				(pos) => pos.marketIndex === selectedMarketIndex
			) ?? null,
		[currentAccountPositions, selectedMarketIndex]
	);

	const quoteSize = Number(quoteSizeStringValue);
	const marketId = selectedMarket?.marketId ?? MarketId.createPerpMarket(0);

	const velocityClient = useVelocityClient();
	const stepSizeDecimalPlaces = velocityClient
		? getMarketStepSizeDecimals(velocityClient, marketId)
		: TRADE_PRECISION;

	const previewOrderParams = usePreviewOrder();

	const { entryPrice } = previewOrderParams.priceImpact || {};

	const stepSize = useMarketStepSize(marketId);
	const sizeDigits = useMarketSizeDigits(marketId);

	const priceBoxBigNum = parseInputBigNumOrZero(
		priceBoxStringValue,
		PRICE_PRECISION_EXP
	);

	const getEntryPrice = () =>
		BigNum.from(entryPrice ? entryPrice : 0, PRICE_PRECISION_EXP);

	const userAccountIsReady = useUserAccountIsReady();

	let currentLiquidationPrice = BigNum.from(
		currentPosition?.liqPrice ?? ZERO,
		PRICE_PRECISION_EXP
	).toNum();

	currentLiquidationPrice =
		currentLiquidationPrice < 0 ? 0 : currentLiquidationPrice;

	const liqPriceAfter = useTradeFormEstimatedLiquidationPrice();

	const previousBaseValue = BigNum.from(
		currentPosition?.baseSize,
		AMM_RESERVE_PRECISION_EXP
	);

	const baseSize = parseInputBigNumOrZero(
		baseSizeStringValue ? baseSizeStringValue : '0',
		AMM_RESERVE_PRECISION_EXP
	);

	const afterBaseValue = previousBaseValue.add(
		baseSize.mul(BigNum.from(side === 'buy' ? new BN(1) : new BN(-1)))
	);

	const previousDirection = previousBaseValue.lt(ZERO)
		? 'SHORT'
		: previousBaseValue.eq(ZERO)
			? ''
			: 'LONG';

	const afterDirection = afterBaseValue.lt(ZERO)
		? 'SHORT'
		: afterBaseValue.eq(ZERO)
			? ''
			: 'LONG';

	const currentStepSize = useCurrentMarketStepSizeBn();
	const currentStepSizeBigNum = BigNum.from(
		currentStepSize,
		BASE_PRECISION_EXP
	);
	const scaledOrdersAveragePrice: BN | null = useMemo(() => {
		try {
			return orderType === 'scaledOrders'
				? calculateAveragePrice(
						sizeDistribution,
						baseSize,
						currentStepSizeBigNum,
						orderCount,
						priceBoxBigNum.val,
						parseInputBigNumOrZero(
							secondaryPriceBoxStringValue,
							PRICE_PRECISION_EXP
						).val
					)
				: null;
		} catch {
			return null;
		}
	}, [
		sizeDistribution,
		baseSize.val,
		orderCount,
		stepSize,
		priceBoxBigNum,
		secondaryPriceBoxStringValue,
	]);

	const limitExitSize = parseInputBigNumOrZero(
		baseSizeStringValue,
		BASE_PRECISION_EXP
	);
	const currentPositionBaseSize = currentPosition
		? BigNum.from(currentPosition.baseSize, BASE_PRECISION_EXP).abs()
		: BigNum.zero(BASE_PRECISION_EXP);

	const orderIsIncreasingPosition =
		currentPosition &&
		((currentPosition.direction === 'short' && side === 'sell') ||
			(currentPosition.direction === 'long' && side === 'buy'));

	return (
		<div className="flex w-full flex-col gap-y-sm">
			{showSlippageTolerance &&
				(isMobileScreenSize ? (
					<BottomDrawerSlippageToleranceInput />
				) : (
					<CollapsibleSlippageToleranceInput dataTestIdPrefix="trade-form-slippage" />
				))}

			<MarginRequiredTradeDetails
				baseSizeStringValue={baseSizeStringValue}
				selectedMarketIndex={selectedMarketIndex}
				estEntryPrice={getEntryPrice().val}
				marginRequired={
					marginRequired
						? BigNum.from(marginRequired, QUOTE_PRECISION_EXP)
						: undefined
				}
			/>

			{!isMobileScreenSize && (
				<ValueDisplay.Default
					label={`Order ${sizeInputSource === 'base' ? 'Value' : 'Size'}`}
					value={
						sizeInputSource === 'base'
							? formatText(quoteSize, PRESETS.usd)
							: `${baseSize.prettyPrint(
									false,
									undefined,
									stepSizeDecimalPlaces
								)} ${baseAssetSymbol}`
					}
				/>
			)}

			{orderType === 'oracleLimit' && <LimitOraclePriceDisplay />}

			{!isMobileScreenSize &&
				orderType === 'limit' &&
				currentPosition &&
				limitExitSize.lte(currentPositionBaseSize) &&
				limitExitSize.gtZero() &&
				!orderIsIncreasingPosition && (
					<EstimatedProfit
						marketId={marketId}
						currentPositionSize={currentPositionBaseSize}
						currentPositionEntryPrice={BigNum.from(
							currentPosition.entryPrice,
							PRICE_PRECISION_EXP
						)}
						currentPositionDirection={currentPosition.direction}
						side={side}
						limitExitSize={limitExitSize}
						limitExitPrice={parseInputBigNumOrZero(
							priceBoxStringValue,
							PRICE_PRECISION_EXP
						)}
					/>
				)}

			{!isMobileScreenSize && (
				<ValueDisplay.ValueChange
					label="Position"
					previousValue={previousBaseValue.abs().toNum()}
					afterValue={afterBaseValue.abs().toNum()}
					previousValuePrint={`${previousBaseValue
						.abs()
						.toNum()
						.toLocaleString('en-US', {
							maximumSignificantDigits: 5,
						})} ${previousDirection}`}
					afterValuePrint={`${capStringFractionDigits(
						afterBaseValue.abs().toNum().toLocaleString('en-US', {
							maximumSignificantDigits: 5,
						}),
						{ maxFractionDigits: sizeDigits }
					)} ${afterDirection}`}
					forceWhite
				/>
			)}

			<LiquidationPriceDisplay
				orderType={orderType}
				liqPriceBefore={currentLiquidationPrice}
				liqPriceBeforeString={
					currentLiquidationPrice !== 0
						? trimDisplayZeros(
								formatText(
									currentLiquidationPrice,
									withDecimals(PRESETS.usdTruncate, marketDecimals)
								)
							)
						: PRICE_FALLBACK_DISPLAY_VALUE
				}
				liqPriceAfter={liqPriceAfter}
				liqPriceAfterString={
					baseSizeStringValue && baseSizeStringValue !== '0'
						? liqPriceAfter !== 0
							? trimDisplayZeros(
									formatText(
										liqPriceAfter,
										withDecimals(PRESETS.usdTruncate, marketDecimals)
									)
								)
							: PRICE_FALLBACK_DISPLAY_VALUE
						: ''
				}
				userAccountIsReady={userAccountIsReady}
				selectedMarketIndex={selectedMarketIndex}
				baseSize={baseSize}
				isMobileScreenSize={isMobileScreenSize}
			/>

			<FeeTierValueDisplay />

			{!isMobileScreenSize && scaledOrdersAveragePrice && (
				<ValueDisplay.Default
					label="Average price"
					value={`$${formatText(
						NumLib.formatBn.toRawNum(scaledOrdersAveragePrice, PRICE_PRECISION),
						PRESETS.displayPrice
					)}`}
				/>
			)}

			{/* Self-gates on swift server health, so it renders for every order type. */}
			{!isMobileScreenSize && <SwiftOfflineIndicator className="ml-auto" />}
		</div>
	);
};

export default React.memo(PerpTradeDetails);
